import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { KeyboardEvent, useRef, useEffect } from 'react';
import { Container, Button, Col } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { FcGoogle } from 'react-icons/fc';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { localStore } from '../../../v2/js/utils/storage';
import { AuthService, UserRegistration, UserTypeEnum } from '../../api';
import { FormCheckboxField } from '../../app/components/FormCheckboxField';
import { FormFeedback } from '../../app/components/FormFeedback';
import { FormInputMaskField } from '../../app/components/FormInputMaskField';
import { FormTextField } from '../../app/components/FormTextField';
import { Logo } from '../../app/components/Logo';
import { PasswordField } from '../../app/components/PasswordField';
import { useDocumentTitle } from '../../app/hooks/use-document-title';
import { setFormErrorsFromApiErrors } from '../../utils/forms';
import { strongPasswordZodSchema } from '../../utils/security';

const baseValidationSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email({
    message: "That email doesn't look quite right.",
  }),
  first_name: z.string().min(1, { message: 'First Name is required' }),
  last_name: z.string().min(1, { message: 'Last Name is required' }),
  phone_number: z.string().min(1, { message: 'Phone Number is required' }),
  password1: strongPasswordZodSchema,
  password2: z.string().min(1, { message: 'Confirm Password is required' }),
  text_opt_in: z.boolean(),
});

const providerValidationSchema = baseValidationSchema.refine(
  ({ password1, password2 }) => password1 === password2,
  {
    path: ['password2'],
    message: "Your passwords don't match.",
  }
);

const repValidationSchema = baseValidationSchema
  .extend({
    healthcare_entity_rep__healthcare_entity__name: z
      .string()
      .min(1, { message: 'Healthcare Entity Name is required' }),
    healthcare_entity_rep__title: z.string().min(1, { message: 'Job Title is required' }),
  })
  .refine(({ password1, password2 }) => password1 === password2, {
    path: ['password2'],
    message: "Your passwords don't match.",
  });

export const AccountSetup = ({
  userType,
  documentTitle,
}: {
  userType: UserTypeEnum;
  documentTitle: string;
}) => {
  useDocumentTitle(documentTitle);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    handleSubmit,
    formState: { errors },
    setError,
    register,
    setValue,
  } = useForm({
    resolver: zodResolver(
      userType === UserTypeEnum.HEALTHCARE_ENTITY_REP
        ? repValidationSchema
        : providerValidationSchema
    ),
  });

  // Capture UTM parameters on component mount
  useEffect(() => {
    const utmParams = Array.from(searchParams.entries()).reduce(
      (acc, [key, value]) => {
        if (key.startsWith('utm_')) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    // Store UTM parameters if any exist
    if (Object.keys(utmParams).length > 0) {
      localStore.set('attributionSignUpCampaign', JSON.stringify(utmParams));
    }
  }, [searchParams]);

  // Capture referral provider and program id when present in URL
  useEffect(() => {
    const referralProvider = searchParams.get('referral_provider');
    const programId = searchParams.get('program_id');
    if (referralProvider && referralProvider.trim().length > 0) {
      localStore.set('attributionReferralProvider', referralProvider);
    }
    if (programId && programId.trim().length > 0) {
      localStore.set('attributionProgramID', programId);
    }
  }, [searchParams]);

  useEffect(() => {
    const allowedFields = ['email', 'first_name', 'last_name', 'phone_number'] as const;
    type PrefillKey = (typeof allowedFields)[number];
    const prefillMap: Array<[PrefillKey, string | null]> = [
      ['email', searchParams.get('email')],
      ['first_name', searchParams.get('first_name')],
      ['last_name', searchParams.get('last_name')],
      ['phone_number', searchParams.get('phone_number')],
    ];

    prefillMap.forEach(([field, value]) => {
      if (value && value.trim().length > 0) {
        setValue(field, value, { shouldValidate: true, shouldDirty: true });
      }
    });
  }, [searchParams, setValue]);

  const mutationRegister = useMutation({
    mutationFn: async (data: UserRegistration) => {
      localStore.remove('session');
      await AuthService.authRegistrationCreate({ requestBody: data });
      localStore.set('authLastSigninEmail', data.email);
    },
  });

  const registerProvider = ({ first_name, last_name, phone_number, ...data }) => {
    const swagScanId = localStore.get('attributionSwagScanId') || localStore.get('swag_scan_id');
    const signUpCampaign = localStore.get('attributionSignUpCampaign');
    const referralProvider = localStore.get('attributionReferralProvider');
    const programId = localStore.get('attributionProgramID');
    const metadata: Record<string, string> = {};
    if (swagScanId) {
      metadata.swag_scan_id = swagScanId;
    }
    if (signUpCampaign) {
      metadata.utm_params = JSON.parse(signUpCampaign);
    }
    return mutationRegister.mutateAsync({
      ...data,
      first_name: first_name,
      last_name: last_name,
      phone_number: phone_number,
      user_type: userType,
      ...(referralProvider && { referral_provider: referralProvider }),
      ...(programId && { program_id: programId }),
      ...(Object.keys(metadata).length > 0 && { metadata }),
    } as UserRegistration);
  };

  const registerRep = ({
    first_name,
    last_name,
    phone_number,
    healthcare_entity_rep__healthcare_entity__name,
    healthcare_entity_rep__title,
    ...data
  }) =>
    mutationRegister.mutateAsync({
      ...data,
      first_name: first_name,
      last_name: last_name,
      phone_number: phone_number,
      healthcare_entity_rep: {
        healthcare_entity: {
          name: healthcare_entity_rep__healthcare_entity__name,
        },
        title: healthcare_entity_rep__title,
      },
      user_type: userType,
    } as UserRegistration);

  // Callback for registration form submission
  const onSubmit = async (data) => {
    try {
      if (!executeRecaptcha) {
        console.warn('Recaptcha not ready yet');
        // Decide: Block submission or allow proceed?
        // Best practice: Fail safely or wait. Here we proceed but log warning.
      }

      let recaptchaToken = '';

      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('registration');
        console.log('Recaptcha token:', recaptchaToken);
      }

      const payload = {
        ...data,
        recaptcha_token: recaptchaToken, // Backend must expect this field
      };
      if (userType === UserTypeEnum.HEALTHCARE_ENTITY_REP) {
        await registerRep(payload);
      } else {
        await registerProvider(payload);
      }

      // Push event to GTM
      if (window.TAG_GOOGLE_TAG_MANAGER_ID) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'registration_complete',
          user_type: userType,
          registration_method: 'manual',
        });
      }

      navigate('/register/email-confirmation');
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, setError);
    }
  };

  // Submit on enter key press
  const handleUserKeyPress = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(onSubmit)();
    }
  };

  return (
    <div className="login-bg-container">
      <Container>
        <Card>
          <Card.Body>
            <div className="logo-container">
              <Logo className="logo" variant="color" />
            </div>
            <h5 className="text-center mb-2 pb-2 text-color-primary-dark">New Provider Account</h5>
            <div className="social-buttons mb-1">
              <Button
                variant="outline-primary"
                size="lg"
                className="w-100 d-flex align-items-center justify-content-center"
                href={`${window.GOOGLE_LOGIN_REDIRECT_URL}?user_type=${UserTypeEnum.PROVIDER}`}
              >
                <FcGoogle className="me-2 mb-1" />
                <span>Create with Google</span>
              </Button>
              <div className="separator separator--no-margin">
                <span>or setup manually</span>
              </div>
            </div>
            <Form
              ref={formRef}
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={handleUserKeyPress}
              className="d-flex flex-column new-account-form"
            >
              <FormFeedback serverErrors={(mutationRegister.error as any)?.body.non_field_errors} />
              <FormFeedback
                clientError={
                  (errors as any).recaptcha_token
                    ? {
                        ...(errors as any).recaptcha_token,
                        message: `reCAPTCHA: ${(errors as any).recaptcha_token.message}`,
                      }
                    : undefined
                }
              />
              <Row>
                <Col>
                  <FormTextField
                    name="first_name"
                    label="First Name"
                    errors={errors}
                    register={register}
                    required
                  />
                </Col>
                <Col>
                  <FormTextField
                    name="last_name"
                    label="Last Name"
                    errors={errors}
                    register={register}
                    required
                  />
                </Col>
              </Row>
              <FormInputMaskField
                name="phone_number"
                label="Phone Number"
                mask="(___) ___-____"
                replacement={{ _: /\d/ }}
                placeholder="(123) 456-7890"
                errors={errors}
                register={register}
                required
              />
              {userType === UserTypeEnum.HEALTHCARE_ENTITY_REP && (
                <>
                  <FormTextField
                    name="healthcare_entity_rep__healthcare_entity__name"
                    label="Healthcare Entity Name"
                    errors={errors}
                    register={register}
                    required
                  />
                  <FormTextField
                    name="healthcare_entity_rep__title"
                    label="Job Title"
                    errors={errors}
                    register={register}
                    required
                  />
                </>
              )}
              <FormTextField
                name="email"
                label="Email"
                errors={errors}
                register={register}
                required
              />
              <PasswordField
                name="password1"
                label="Password"
                errors={errors}
                register={register}
                required
              />
              <PasswordField
                name="password2"
                label="Confirm Password"
                errors={errors}
                register={register}
                required
              />
              <Row>
                <Col>
                  <div className="d-flex align-items-start mb-2">
                    <FormCheckboxField
                      name="text_opt_in"
                      label=""
                      errors={errors}
                      register={register}
                      className="mb-0 flex-shrink-0 d-inline-flex"
                    />
                    <small className="text-muted">
                      I consent to receive notifications from Sendit about account activity via SMS.
                      Message & data rates may apply. Message frequency depends on account activity.
                      Reply STOP to opt out, HELP for help.{' '}
                      <a
                        href="https://www.senditgigs.com/terms-of-service/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Use
                      </a>{' '}
                      &{' '}
                      <a
                        href="https://www.senditgigs.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </small>
                  </div>
                </Col>
              </Row>
              <div className="d-grid mt-2">
                <Button
                  size="lg"
                  type="submit"
                  variant="primary"
                  onClick={() => handleSubmit(onSubmit)()}
                  disabled={mutationRegister.isLoading}
                >
                  Create Account
                </Button>
              </div>
              <div className="text-center my-3">
                Already have an account? <RouterLink to="/login">Sign in</RouterLink>
              </div>
              <div className="bg-primary-muted rounded px-3 py-2 mb-1 mt-0 small">
                If you are a Healthcare Organization Representative, please{' '}
                <a href="https://www.senditgigs.com/get-in-touch/">contact us</a> to get started.
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};
